/**
 * Loan Opening — Field Changes.
 *
 * Backend: eastview-os run-agent-reviews.md "Field-level diff + per-field revert".
 *   GET  /admin/loans/{dealId}/ai-reviews/field-writes            → the diff table
 *   POST .../field-writes/{provenanceId}/revert                   → undo one write
 *
 * The Loan Opening agent auto-populates empty loan fields; this shows the exact
 * per-field diff (field, old → new, source) it produced, with a per-row Revert.
 * Revert is GUARDED server-side — it returns 409 if the field was edited after
 * the agent wrote it, so a human change is never clobbered. Admin-only (the tab
 * that mounts it is admin-gated, matching the endpoints' require_admin).
 *
 * Rows whose `revertOf` is set are the reversal audit entries themselves, not
 * agent populations — filtered out so the table shows only what the agent wrote.
 *
 * UI: the card header is a collapse toggle whose sub-line previews the summary
 * ("32 fields populated · from 12 documents"). Expanded, a segmented switcher
 * offers three groupings of the same changes:
 *   • By document — accordion per source document (which doc wrote what).
 *   • By field    — accordion per entity key-path root (subjectProperties.0,
 *                   borrowers.1, entities.0, …); each row also names the doc.
 *   • List        — one flat list, each row naming its source document.
 * This is a grouped provenance display, not a flat multi-column records grid, so
 * the AG-Grid-for-tables convention doesn't apply — the value is in the grouping.
 */
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import EditNoteOutlined from "@mui/icons-material/EditNoteOutlined";
import SearchRounded from "@mui/icons-material/SearchRounded";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import HomeWorkOutlined from "@mui/icons-material/HomeWorkOutlined";
import BusinessOutlined from "@mui/icons-material/BusinessOutlined";
import PersonOutlineRounded from "@mui/icons-material/PersonOutlineRounded";
import AccountBalanceOutlined from "@mui/icons-material/AccountBalanceOutlined";
import RestoreRounded from "@mui/icons-material/RestoreRounded";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import ContactPageOutlined from "@mui/icons-material/ContactPageOutlined";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import AddCircleOutlineRounded from "@mui/icons-material/AddCircleOutlineRounded";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { losAiReviewsApi } from "../../../api/losAiReviewsApi";
import { formatDate } from "../../../_src_shared/utils/functions/formatDate";
import { formatDateTime } from "../../../_src_shared/utils/functions/formatDateTime";
import { filePreviewSetData } from "../../../redux/reducers/filePreviewReducer";

// --- Field-path + value formatting ----------------------------------------

const ACRONYMS = {
  apn: "APN",
  id: "ID",
  llc: "LLC",
  uw: "UW",
  hud: "HUD",
  sq: "Sq",
};
const CURRENCY_HINTS =
  /budget|insurance|taxes|cost|amount|price|premium|balance|payoff|reserve/i;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

// Entity-array roots we split by index for the "By field" grouping + context.
const FIELD_ROOTS = {
  subjectProperties: "Property",
  entities: "Entity",
  borrowers: "Borrower",
  guarantors: "Guarantor",
};

const titleize = (segment) =>
  segment
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(" ")
    .map(
      (w) =>
        ACRONYMS[w.toLowerCase()] || w.charAt(0).toUpperCase() + w.slice(1),
    )
    .join(" ");

// "subjectProperties.0.address.apn" -> { label: "APN", context: "Property 1" }
const humanizeField = (path) => {
  const parts = String(path || "").split(".");
  let context = null;
  let rest = parts;
  if (FIELD_ROOTS[parts[0]] && /^\d+$/.test(parts[1])) {
    context = `${FIELD_ROOTS[parts[0]]} ${Number(parts[1]) + 1}`;
    rest = parts.slice(2);
  }
  const leaf = rest[rest.length - 1] || path;
  return { label: titleize(leaf), context };
};

// "subjectProperties.0.address.apn" -> { key: "subjectProperties.0",
// label: "Property 1", root: "subjectProperties", index: 0 }. Top-level scalar
// fields (amortizationType, …) collapse into a single "Loan Details" group.
const fieldGroupOf = (path) => {
  const parts = String(path || "").split(".");
  if (FIELD_ROOTS[parts[0]] && /^\d+$/.test(parts[1])) {
    return {
      key: `${parts[0]}.${parts[1]}`,
      label: `${FIELD_ROOTS[parts[0]]} ${Number(parts[1]) + 1}`,
      root: parts[0],
      index: Number(parts[1]),
    };
  }
  return { key: "loan", label: "Loan Details", root: "loan", index: null };
};

// The human name of the entity a field-group points at, read off the live loan
// (borrowers/guarantors → first+last, properties → address, entities → name).
// Returns null when there's no name to show (e.g. the "Loan Details" group).
const entityNameFor = (root, index, loan) => {
  if (index == null || !loan) return null;
  if (root === "subjectProperties") {
    return loan.subjectProperties?.[index]?.address?.fullAddress || null;
  }
  if (root === "entities") {
    return loan.entities?.[index]?.entityName || null;
  }
  if (root === "borrowers" || root === "guarantors") {
    const b = loan[root]?.[index];
    const name = `${b?.firstName || ""} ${b?.lastName || ""}`.trim();
    return name || null;
  }
  return null;
};

const GROUP_ICON = {
  subjectProperties: HomeWorkOutlined,
  entities: BusinessOutlined,
  borrowers: PersonOutlineRounded,
  guarantors: PersonOutlineRounded,
  loan: AccountBalanceOutlined,
};

// --- Nested values ---------------------------------------------------------
// Extracted values are not always scalars: bankDetails / entityAddress /
// primaryAddress arrive as objects, entityMembers + housing_event_types as
// arrays. A naive String()/Object.values() render produced "[object Object]",
// so complex values get a one-line SUMMARY in the row plus an expandable
// key/value tree underneath (see ValueTree / ChangeRow).

const isPlainObject = (v) =>
  v !== null && typeof v === "object" && !Array.isArray(v);

// "Complex" = needs the expandable tree. Arrays of plain scalars don't.
const isComplexValue = (v) =>
  isPlainObject(v) ||
  (Array.isArray(v) &&
    v.length > 0 &&
    v.some((item) => item !== null && typeof item === "object"));

const isEmptyValue = (v) =>
  v === null ||
  v === undefined ||
  v === "" ||
  (Array.isArray(v) && v.length === 0) ||
  (isPlainObject(v) && Object.keys(v).length === 0);

// Keys that, when present, ARE the object's human identity ("16436 Flanders
// Street…", "113 Mountain Estate LLC"). Checked in priority order.
const NAME_KEYS = [
  "fullAddress",
  "entityName",
  "fullName",
  "nameOfBank",
  "name",
  "title",
  "label",
  "description",
];

// One-line stand-in for an object: its identity key if it has one, otherwise
// the first couple of populated fields plus a "+N more" tail.
const summarizeObject = (obj) => {
  for (const key of NAME_KEYS) {
    const v = obj[key];
    if (v && typeof v !== "object") return String(v);
  }
  const person = `${obj.firstName || ""} ${obj.lastName || ""}`.trim();
  if (person) return person;

  const entries = Object.entries(obj).filter(([, v]) => !isEmptyValue(v));
  if (!entries.length) return null;
  const shown = entries
    .slice(0, 2)
    // eslint-disable-next-line no-use-before-define
    .map(([k, v]) => `${titleize(k)}: ${formatValue(v, k) ?? "—"}`);
  const extra = entries.length - shown.length;
  return extra > 0 ? `${shown.join(" · ")} · +${extra} more` : shown.join(" · ");
};

// Render any extracted value (string / number / bool / empty / object / array /
// date) as a single display string. Objects and arrays-of-objects collapse to a
// summary — the full shape is available via the expandable tree.
const formatValue = (value, fieldPath = "") => {
  if (isEmptyValue(value)) return null;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") {
    const formatted = value.toLocaleString("en-US");
    return CURRENCY_HINTS.test(fieldPath) ? `$${formatted}` : formatted;
  }
  if (Array.isArray(value)) {
    const parts = value.map((item) =>
      isPlainObject(item)
        ? summarizeObject(item) || "—"
        : formatValue(item, fieldPath),
    );
    return parts.filter(Boolean).join(", ") || null;
  }
  if (typeof value === "object") return summarizeObject(value);
  if (typeof value === "string" && ISO_DATE.test(value)) {
    // Date-only (midnight) → timezone-stable date; otherwise a real timestamp.
    const isMidnight =
      value.slice(11, 19) === "00:00:00" || value.slice(11, 16) === "00:00";
    return isMidnight
      ? formatDate(value, value)
      : formatDateTime(value) || value;
  }
  return String(value);
};

// Flatten a nested value (keys + leaf values) into one searchable string.
const deepSearchText = (value) => {
  if (isEmptyValue(value)) return "";
  if (Array.isArray(value)) return value.map(deepSearchText).join(" ");
  if (isPlainObject(value)) {
    return Object.entries(value)
      .map(([k, v]) => `${titleize(k)} ${deepSearchText(v)}`)
      .join(" ");
  }
  return String(value);
};

// "5 fields" / "2 items" — the badge on the expand toggle.
const complexityLabel = (value) => {
  if (Array.isArray(value)) {
    const n = value.length;
    return `${n} ${n === 1 ? "item" : "items"}`;
  }
  if (isPlainObject(value)) {
    const n = Object.keys(value).filter((k) => !isEmptyValue(value[k])).length;
    return `${n} ${n === 1 ? "field" : "fields"}`;
  }
  return null;
};

// Section heading inside an expanded nested value ("Before" / "After").
const DETAIL_HEADING_SX = {
  display: "block",
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "#94a3b8",
  mb: 0.75,
};

// Recursive key/value renderer for the expanded detail of a nested value.
const ValueTree = ({ value, fieldPath = "" }) => {
  if (isEmptyValue(value)) {
    return (
      <Txt sx={{ fontSize: 11.5, fontStyle: "italic", color: "#94a3b8" }}>
        empty
      </Txt>
    );
  }

  if (Array.isArray(value)) {
    return (
      <Flx column sx={{ gap: 0.75 }}>
        {value.map((item, i) => {
          const complex = isComplexValue(item);
          return (
            <Box
              key={i}
              sx={
                complex
                  ? {
                      border: "1px solid #eef1f5",
                      borderRadius: "8px",
                      bgcolor: "#fff",
                      p: 1,
                    }
                  : undefined
              }
            >
              {complex && (
                <Txt
                  sx={{
                    display: "block",
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    color: "#94a3b8",
                    mb: 0.5,
                  }}
                >
                  Item {i + 1}
                </Txt>
              )}
              <ValueTree value={item} fieldPath={fieldPath} />
            </Box>
          );
        })}
      </Flx>
    );
  }

  if (isPlainObject(value)) {
    const entries = Object.entries(value);
    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "minmax(110px, 190px) minmax(0, 1fr)",
          gap: "4px 12px",
          alignItems: "baseline",
        }}
      >
        {entries.map(([k, v]) => (
          <Fragment key={k}>
            <Txt
              sx={{
                fontSize: 11.5,
                fontWeight: 600,
                color: "#64748b",
                wordBreak: "break-word",
              }}
            >
              {titleize(k)}
            </Txt>
            {isComplexValue(v) ? (
              <ValueTree value={v} fieldPath={k} />
            ) : (
              <Txt
                sx={{
                  fontSize: 11.5,
                  color: isEmptyValue(v) ? "#94a3b8" : "#0f172a",
                  fontStyle: isEmptyValue(v) ? "italic" : "normal",
                  wordBreak: "break-word",
                }}
              >
                {formatValue(v, k) ?? "empty"}
              </Txt>
            )}
          </Fragment>
        ))}
      </Box>
    );
  }

  return (
    <Txt sx={{ fontSize: 11.5, color: "#0f172a", wordBreak: "break-word" }}>
      {formatValue(value, fieldPath)}
    </Txt>
  );
};

// The "5 fields ⌄" pill that opens a nested value's detail tree.
const ExpandValueToggle = ({ value, open, onToggle }) => (
  <Box
    component="button"
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      onToggle();
    }}
    sx={{
      flexShrink: 0,
      display: "inline-flex",
      alignItems: "center",
      gap: "2px",
      border: "1px solid #e5e8ee",
      borderRadius: "999px",
      bgcolor: open ? "#f5f3ff" : "#fff",
      color: open ? "#7c3aed" : "#64748b",
      cursor: "pointer",
      font: "inherit",
      fontSize: 10,
      fontWeight: 700,
      lineHeight: 1.4,
      px: 0.75,
      py: "1px",
      "&:hover": { color: "#7c3aed", borderColor: "#e6e0fb" },
    }}
  >
    {complexityLabel(value)}
    <ExpandMoreRounded
      sx={{
        fontSize: 13,
        transition: "transform 0.15s ease",
        transform: open ? "rotate(180deg)" : "none",
      }}
    />
  </Box>
);

// Strip extension + underscores from filenames for display.
const prettyDocName = (name) =>
  String(name || "")
    .replace(/\.pdf$/i, "")
    .replace(/_/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();

// Small pill: "N fields".
const CountPill = ({ n }) => (
  <Box
    component="span"
    sx={{
      flexShrink: 0,
      fontSize: 11,
      fontWeight: 600,
      color: "#64748b",
      bgcolor: "#fff",
      border: "1px solid #e5e8ee",
      borderRadius: "999px",
      px: 1,
      py: "1px",
    }}
  >
    {n} {n === 1 ? "field" : "fields"}
  </Box>
);

// --- One field-change row --------------------------------------------------
// showDoc: give the source document its own column (used by the By-field and
// List views, where the document isn't already the group header).
// hideContext: suppress the "Property 1" chip when the group header already says it.

const ChangeRow = ({
  row,
  onRevert,
  onOpenDoc,
  showDoc = false,
  hideContext = false,
  gridTemplate = null,
}) => {
  const { label, context } = humanizeField(row.field);
  const oldVal = formatValue(row.old, row.field);
  const newVal = formatValue(row.new, row.field);
  const reverted = !!row.reverted;
  const canOpen = !!row.sourceDocOid && !!onOpenDoc;
  // Nested object/array values keep their summary in the row and expose the
  // full shape in a tree below it.
  const newIsComplex = isComplexValue(row.new);
  const oldIsComplex = isComplexValue(row.old);
  const [detailOpen, setDetailOpen] = useState(false);

  return (
    <Box
      sx={{
        borderTop: "1px solid #f1f5f9",
        "&:first-of-type": { borderTop: "none" },
        bgcolor: reverted ? "#fafbfc" : "transparent",
      }}
    >
      <Box
        sx={{
          display: "grid",
          // `gridTemplate` (List view) overrides the responsive default so rows
          // line up with the resizable header.
          gridTemplateColumns:
            gridTemplate ||
            (showDoc
              ? "minmax(140px, 1fr) minmax(170px, 1.3fr) minmax(120px, 0.9fr) auto"
              : "minmax(150px, 1fr) minmax(200px, 1.6fr) auto"),
          alignItems: "center",
          gap: "0 16px",
          px: 2,
          py: 1.25,
        }}
      >
        {/* Field */}
        <Box sx={{ minWidth: 0 }}>
          <Flx ac sx={{ gap: 0.75, minWidth: 0 }}>
            <Txt
              sx={{
                fontSize: 13,
                fontWeight: 500,
                color: reverted ? "#94a3b8" : "#1e293b",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </Txt>
            {!hideContext && context && (
              <Box
                component="span"
                sx={{
                  flexShrink: 0,
                  fontSize: 10,
                  fontWeight: 600,
                  color: "#64748b",
                  bgcolor: "#f1f5f9",
                  borderRadius: "4px",
                  px: 0.5,
                  py: "1px",
                }}
              >
                {context}
              </Box>
            )}
          </Flx>
          <Txt
            title={row.field}
            sx={{
              fontSize: 11,
              fontFamily: "ui-monospace, monospace",
              color: "#94a3b8",
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.field}
          </Txt>
        </Box>

        {/* Value diff: old → new. Nested values show a summary + expand pill. */}
        <Flx ac sx={{ gap: 0.75, minWidth: 0 }}>
          <Txt
            title={oldIsComplex ? oldVal || undefined : undefined}
            sx={{
              fontSize: 11,
              fontStyle: "italic",
              color: "#94a3b8",
              flexShrink: oldIsComplex ? 1 : 0,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {oldVal ?? "empty"}
          </Txt>
          <Box component="span" sx={{ color: "#cbd5e1", flexShrink: 0 }}>
            →
          </Box>
          <Txt
            title={typeof newVal === "string" ? newVal : undefined}
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: reverted ? "#94a3b8" : "#0f172a",
              textDecoration: reverted ? "line-through" : "none",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {newVal ?? "empty"}
          </Txt>
          {(newIsComplex || oldIsComplex) && (
            <ExpandValueToggle
              value={newIsComplex ? row.new : row.old}
              open={detailOpen}
              onToggle={() => setDetailOpen((v) => !v)}
            />
          )}
        </Flx>

        {/* Source document — its own column in the By-field / List views. */}
        {showDoc && (
          <Flx ac sx={{ gap: 0.5, minWidth: 0 }}>
            <DescriptionOutlined
              sx={{ fontSize: 13, color: "#94a3b8", flexShrink: 0 }}
            />
            <Box
              component="span"
              onClick={canOpen ? () => onOpenDoc(row) : undefined}
              title={row.sourceDocName}
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: canOpen ? "#2563eb" : "#64748b",
                cursor: canOpen ? "pointer" : "default",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                "&:hover": canOpen
                  ? { textDecoration: "underline" }
                  : undefined,
              }}
            >
              {prettyDocName(row.sourceDocName) ||
                row.sourceDocType ||
                "Source document"}
            </Box>
          </Flx>
        )}

        {/* When + action */}
        <Flx ac sx={{ gap: 1.5, justifyContent: "flex-end", flexShrink: 0 }}>
          <Txt sx={{ fontSize: 11, color: "#94a3b8", whiteSpace: "nowrap" }}>
            {formatDateTime(row.ts) || ""}
          </Txt>
          {reverted ? (
            <Chip
              size="small"
              label="Reverted"
              sx={{
                fontSize: 10.5,
                fontWeight: 600,
                height: 20,
                bgcolor: "#f1f5f9",
                color: "#64748b",
              }}
            />
          ) : (
            <Button
              size="small"
              onClick={() => onRevert(row)}
              startIcon={
                <RestoreRounded sx={{ fontSize: "14px !important", mr: -0.5 }} />
              }
              sx={{
                textTransform: "none",
                minWidth: 0,
                px: 1,
                fontSize: 11,
                fontWeight: 600,
                color: "#64748b",
                bgcolor: "transparent",
                "&:hover": { color: "#dc2626", bgcolor: "#fef2f2" },
              }}
            >
              Revert
            </Button>
          )}
        </Flx>
      </Box>

      {/* Expanded nested value — before/after trees, full row width. */}
      <Collapse in={detailOpen} unmountOnExit>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: oldIsComplex
              ? { xs: "1fr", md: "1fr 1fr" }
              : "1fr",
            gap: "12px 20px",
            mx: 2,
            mb: 1.5,
            p: 1.5,
            borderRadius: "8px",
            border: "1px dashed #e3e9ec",
            bgcolor: "#fbfcfe",
          }}
        >
          {oldIsComplex && (
            <Box sx={{ minWidth: 0 }}>
              <Txt sx={DETAIL_HEADING_SX}>Before</Txt>
              <ValueTree value={row.old} fieldPath={row.field} />
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Txt sx={DETAIL_HEADING_SX}>{oldIsComplex ? "After" : "Value"}</Txt>
            <ValueTree value={row.new} fieldPath={row.field} />
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

// --- Generic collapsible group shell (shared by doc + field groupings) ------

const CollapsibleGroup = ({
  icon: Icon,
  title,
  titleAttr,
  subtitle,
  meta,
  rightAction,
  defaultOpen,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box
      sx={{
        border: "1px solid #e5e8ee",
        borderRadius: "10px",
        overflow: "hidden",
        bgcolor: "#fff",
      }}
    >
      <Flx
        ac
        sx={{
          gap: 1,
          px: 2,
          py: 1.25,
          borderBottom: open ? "1px solid #eef1f5" : "none",
        }}
      >
        <Flx
          ac
          onClick={() => setOpen((v) => !v)}
          sx={{
            gap: 1,
            minWidth: 0,
            flex: 1,
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          {open ? (
            <ExpandMoreRounded
              sx={{ fontSize: 18, color: "#94a3b8", flexShrink: 0 }}
            />
          ) : (
            <ChevronRightRounded
              sx={{ fontSize: 18, color: "#94a3b8", flexShrink: 0 }}
            />
          )}
          {Icon && (
            <Icon sx={{ fontSize: 16, color: "#94a3b8", flexShrink: 0 }} />
          )}
          <Txt
            title={titleAttr}
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: "#334155",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {title}
          </Txt>
          {subtitle && (
            <Txt
              title={subtitle}
              sx={{
                fontSize: 12.5,
                fontWeight: 500,
                color: "#64748b",
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {subtitle}
            </Txt>
          )}
          {meta}
        </Flx>
        {rightAction}
      </Flx>

      <Collapse in={open} unmountOnExit>
        <Box>{children}</Box>
      </Collapse>
    </Box>
  );
};

// --- Whole-object creations (borrower / entity / property / appraiser) ------
// The opening agent can CREATE missing objects, not just fill fields. Those
// arrive on the same feed with objectCreated:true — render as a "Created <kind>"
// card (not an old→new diff) with a Remove action (the same revert endpoint
// deletes the object). See run-agent-reviews.md "Whole-object creations".

const OBJECT_KIND = {
  borrower: { label: "Borrower", Icon: PersonOutlineRounded },
  entity: { label: "Entity", Icon: BusinessOutlined },
  subject_property: { label: "Property", Icon: HomeWorkOutlined },
  appraiser_contact: { label: "Appraiser Contact", Icon: ContactPageOutlined },
};

const ObjectCreatedCard = ({ row, first, onRemove, onOpenDoc }) => {
  const kind = OBJECT_KIND[row.objectKind] || {
    label: titleize(row.objectKind || "Object"),
    Icon: AddCircleOutlineRounded,
  };
  const KindIcon = kind.Icon;
  const removed = !!row.reverted;
  // Creations carry the created object itself on `new` (or on `old` once the
  // reversal is recorded) — summarize whichever side is populated.
  const payload = isEmptyValue(row.new) ? row.old : row.new;
  const summary = formatValue(payload, row.field) || "—";
  const canOpen = !!row.sourceDocOid && !!onOpenDoc;
  const [detailOpen, setDetailOpen] = useState(false);

  return (
    <Box
      sx={{
        borderTop: first ? "none" : "1px solid #f1f5f9",
        bgcolor: removed ? "#fafbfc" : "transparent",
      }}
    >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1.5,
      }}
    >
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: "8px",
          bgcolor: "#f5f3ff",
          color: "#7c3aed",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <KindIcon sx={{ fontSize: 17 }} />
      </Box>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Flx ac sx={{ gap: 0.75, flexWrap: "wrap" }}>
          <Txt
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: removed ? "#94a3b8" : "#0f172a",
            }}
          >
            Created {kind.label}
          </Txt>
          <Txt
            title={typeof summary === "string" ? summary : undefined}
            sx={{
              fontSize: 13,
              color: removed ? "#94a3b8" : "#334155",
              textDecoration: removed ? "line-through" : "none",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {summary}
          </Txt>
          {isComplexValue(payload) && (
            <ExpandValueToggle
              value={payload}
              open={detailOpen}
              onToggle={() => setDetailOpen((v) => !v)}
            />
          )}
        </Flx>
        <Flx ac sx={{ gap: 0.75, mt: "2px", flexWrap: "wrap" }}>
          {row.sourceDocName && (
            <Box
              component="span"
              onClick={canOpen ? () => onOpenDoc(row) : undefined}
              title={row.sourceDocName}
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: canOpen ? "#2563eb" : "#94a3b8",
                cursor: canOpen ? "pointer" : "default",
                "&:hover": canOpen ? { textDecoration: "underline" } : undefined,
              }}
            >
              {prettyDocName(row.sourceDocName) || row.sourceDocType}
            </Box>
          )}
          {row.sourceDocName && (
            <Box component="span" sx={{ color: "#cbd5e1" }}>
              ·
            </Box>
          )}
          <Txt sx={{ fontSize: 11, color: "#94a3b8" }}>
            {formatDateTime(row.ts) || ""}
          </Txt>
        </Flx>
      </Box>

      {removed ? (
        <Chip
          size="small"
          label="Removed"
          sx={{
            fontSize: 10.5,
            fontWeight: 600,
            height: 20,
            bgcolor: "#f1f5f9",
            color: "#64748b",
            flexShrink: 0,
          }}
        />
      ) : (
        <Button
          size="small"
          onClick={() => onRemove(row)}
          startIcon={
            <DeleteOutlineRounded sx={{ fontSize: "15px !important", mr: -0.5 }} />
          }
          sx={{
            textTransform: "none",
            minWidth: 0,
            px: 1,
            flexShrink: 0,
            fontSize: 11,
            fontWeight: 600,
            color: "#64748b",
            bgcolor: "transparent",
            "&:hover": { color: "#dc2626", bgcolor: "#fef2f2" },
          }}
        >
          Remove
        </Button>
      )}
      </Box>

      {/* Expanded detail — the full shape of the created object. */}
      <Collapse in={detailOpen} unmountOnExit>
        <Box
          sx={{
            mx: 2,
            mb: 1.5,
            p: 1.5,
            borderRadius: "8px",
            border: "1px dashed #e3e9ec",
            bgcolor: "#fbfcfe",
          }}
        >
          <ValueTree value={payload} fieldPath={row.field} />
        </Box>
      </Collapse>
    </Box>
  );
};

// --- Panel -----------------------------------------------------------------

const VIEW_MODES = [
  { key: "field", label: "By field" },
  { key: "document", label: "By document" },
  { key: "list", label: "List" },
];

// Summary-line stats double as filters over the feed. `test` is the predicate
// for both the header count and the filtered rows, so the number on a chip is
// always exactly what selecting it shows.
const STATUS_ALL = "all";
const STATUS_FILTERS = [
  {
    key: "populated",
    noun: (n) => `${n === 1 ? "field" : "fields"} populated`,
    test: (r) => !r.objectCreated && !r.reverted,
  },
  {
    key: "created",
    noun: () => "created",
    test: (r) => r.objectCreated && !r.reverted,
  },
  {
    key: "reverted",
    noun: () => "reverted",
    test: (r) => !!r.reverted,
  },
];
const statusTest = (key) =>
  STATUS_FILTERS.find((f) => f.key === key)?.test || (() => true);

// One clickable stat in the header: "<count> <label>". Selecting it filters the
// body to that slice; selecting it again clears back to everything.
const SummaryStat = ({ count, label, active, onClick, title }) => (
  <Box
    component="button"
    type="button"
    title={title}
    onClick={(e) => {
      e.stopPropagation(); // the header itself toggles the panel
      onClick();
    }}
    sx={{
      display: "inline-flex",
      alignItems: "baseline",
      gap: "4px",
      border: "1px solid",
      borderColor: active ? "#e6e0fb" : "transparent",
      borderRadius: "999px",
      bgcolor: active ? "#f5f3ff" : "transparent",
      cursor: "pointer",
      font: "inherit",
      fontSize: 12.5,
      color: active ? "#7c3aed" : "#64748b",
      px: active ? 0.875 : 0.375,
      py: "1px",
      "&:hover": {
        bgcolor: active ? "#f5f3ff" : "#f1f5f9",
        color: active ? "#7c3aed" : "#334155",
      },
    }}
  >
    <Box
      component="span"
      sx={{ fontWeight: 700, color: active ? "#7c3aed" : "#0f172a" }}
    >
      {count}
    </Box>
    {label}
  </Box>
);

const OpeningFieldWritesPanel = ({ onCount }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const loan = useSelector((state) => state.loanDrilldown);
  const documents = useSelector((state) => state.loanDrilldown?.loanDocuments);
  const dealId = loan?._id;

  const [rows, setRows] = useState(null); // null = not loaded yet
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false); // whole panel collapsed by default
  const [viewMode, setViewMode] = useState("field");
  const [search, setSearch] = useState("");
  // Which slice of the feed to show — driven by the summary-line stats, which
  // double as filter chips ("49 fields populated · 1 created · 9 reverted").
  const [statusFilter, setStatusFilter] = useState(STATUS_ALL);
  // Resizable column widths (px) for the List view; last column stays `auto`.
  const [listCols, setListCols] = useState({
    field: 240,
    change: 320,
    source: 200,
  });
  const revertingRef = useRef(new Set()); // provenanceIds mid-revert (dedupe)

  // Drag-to-resize a List column: adjust its width from the pointer delta.
  const startResize = useCallback(
    (colKey) => (e) => {
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startW = listCols[colKey];
      const onMove = (ev) =>
        setListCols((c) => ({
          ...c,
          [colKey]: Math.max(90, startW + (ev.clientX - startX)),
        }));
      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [listCols],
  );
  const listTemplate = `${listCols.field}px ${listCols.change}px ${listCols.source}px auto`;

  const fetchRows = useCallback(async () => {
    if (!dealId) return;
    setLoading(true);
    try {
      const data = await losAiReviewsApi.getFieldWrites(dealId);
      // Only the agent populations — drop the reversal audit rows.
      setRows((Array.isArray(data) ? data : []).filter((r) => !r.revertOf));
    } catch {
      // Endpoint unreachable / nothing recorded — treat as empty.
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleRevert = useCallback(
    async (row) => {
      const id = row?.provenanceId;
      if (!dealId || !id || revertingRef.current.has(id)) return;
      revertingRef.current.add(id);
      const kindLabel = (
        OBJECT_KIND[row.objectKind]?.label || "object"
      ).toLowerCase();
      try {
        await losAiReviewsApi.revertFieldWrite(dealId, id);
        enqueueSnackbar(
          row.objectCreated
            ? `Removed the created ${kindLabel}.`
            : `Reverted ${row.field} to its prior value.`,
          { variant: "success" },
        );
        fetchRows();
      } catch (err) {
        const status = err?.response?.status;
        const reason = err?.response?.data?.reason;
        // 409 = guarded: object already removed, or the field was edited after
        // the agent wrote it (no clobbering).
        enqueueSnackbar(
          status === 409
            ? reason ||
                (row.objectCreated
                  ? `Can't remove — this ${kindLabel} was already removed or changed.`
                  : "Can't revert — the field was changed after the agent wrote it.")
            : err?.response?.data?.detail ||
                err?.message ||
                (row.objectCreated
                  ? "Could not remove this object."
                  : "Could not revert this field."),
          { variant: status === 409 ? "warning" : "error" },
        );
      } finally {
        revertingRef.current.delete(id);
      }
    },
    [dealId, enqueueSnackbar, fetchRows],
  );

  // Open the field-write's source document in the docked file-preview sidebar by
  // resolving sourceDocOid against the loan's documents (matches _id or oid).
  const handleOpenDoc = useCallback(
    (row) => {
      const oid = row?.sourceDocOid;
      if (!oid || !Array.isArray(documents)) return;
      const doc = documents.find(
        (d) => String(d?._id) === String(oid) || String(d?.oid) === String(oid),
      );
      if (doc) dispatch(filePreviewSetData(doc));
      else
        enqueueSnackbar("That source document isn't on this loan anymore.", {
          variant: "info",
        });
    },
    [documents, dispatch, enqueueSnackbar],
  );

  // Status chip first (populated / created / reverted), then the text search
  // across field path/label/context, both values, and the source doc.
  const filteredRows = useMemo(() => {
    const inStatus = statusTest(statusFilter);
    const scoped = (rows || []).filter(inStatus);
    const q = search.trim().toLowerCase();
    if (!q) return scoped;
    return scoped.filter((r) => {
      const { label, context } = humanizeField(r.field);
      const hay = [
        r.field,
        label,
        context,
        formatValue(r.old, r.field),
        formatValue(r.new, r.field),
        // Nested values only surface a summary above — search their full shape
        // so "Robinhood" matches a bankDetails write.
        isComplexValue(r.old) ? deepSearchText(r.old) : null,
        isComplexValue(r.new) ? deepSearchText(r.new) : null,
        r.sourceDocName,
        r.sourceDocType,
        r.objectKind,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [rows, search, statusFilter]);

  // Whole-object creations render as their own "Created" cards; the field-diff
  // views only deal with actual field writes.
  const objectRows = useMemo(
    () => filteredRows.filter((r) => r.objectCreated),
    [filteredRows],
  );
  const fieldRows = useMemo(
    () => filteredRows.filter((r) => !r.objectCreated),
    [filteredRows],
  );

  // Group by source document, preserving first-seen order.
  const byDocument = useMemo(() => {
    const map = new Map();
    for (const r of fieldRows) {
      if (!map.has(r.sourceDoc)) {
        map.set(r.sourceDoc, {
          doc: {
            id: r.sourceDoc,
            oid: r.sourceDocOid,
            name: r.sourceDocName,
            type: r.sourceDocType,
          },
          rows: [],
        });
      }
      map.get(r.sourceDoc).rows.push(r);
    }
    return [...map.values()];
  }, [fieldRows]);

  // Group by entity key-path root (subjectProperties.0, borrowers.1, …),
  // labelling each group with the entity's real name from the live loan.
  const byField = useMemo(() => {
    const map = new Map();
    for (const r of fieldRows) {
      const g = fieldGroupOf(r.field);
      if (!map.has(g.key)) {
        map.set(g.key, {
          ...g,
          name: entityNameFor(g.root, g.index, loan),
          rows: [],
        });
      }
      map.get(g.key).rows.push(r);
    }
    return [...map.values()];
  }, [fieldRows, loan]);

  const total = rows?.length || 0;
  const filteredTotal = filteredRows.length;
  // Counts for the summary chips — computed with the same predicates the chips
  // filter by, off the unfiltered feed so the numbers never move as you filter.
  const statusCounts = useMemo(() => {
    const counts = {};
    for (const f of STATUS_FILTERS) {
      counts[f.key] = (rows || []).filter(f.test).length;
    }
    return counts;
  }, [rows]);
  const docCount = byDocument.length;
  // The chip whose slice is showing (null when everything is).
  const activeFilter = STATUS_FILTERS.find((f) => f.key === statusFilter);

  // Selecting a stat filters to it; selecting it again clears. Either way the
  // panel opens, since the result is only visible expanded.
  const toggleStatus = useCallback((key) => {
    setStatusFilter((cur) => (cur === key ? STATUS_ALL : key));
    setExpanded(true);
  }, []);

  // Report the field-change count up to the section-tab badge (Modified Loan Data).
  useEffect(() => {
    onCount?.(total);
  }, [onCount, total]);

  // Hide the whole card when the Loan Opening agent has written nothing (or the
  // fetch hasn't landed yet) — no empty state, just don't render it.
  if (total === 0) return null;

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #e5e8ee",
        borderRadius: "14px",
        boxShadow:
          "0 1px 2px rgba(15,23,42,0.04), 0 8px 24px -12px rgba(15,23,42,0.08)",
        overflow: "hidden",
      }}
    >
      {/* Header — the collapse toggle. Sub-line previews the populated summary. */}
      <Flx
        ac
        onClick={() => setExpanded((v) => !v)}
        sx={{
          gap: 1.5,
          p: "18px 24px",
          cursor: "pointer",
          userSelect: "none",
          borderBottom: expanded ? "1px solid #eef1f5" : "none",
          "&:hover": { bgcolor: "#fafbfc" },
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: "9px",
            background: "linear-gradient(135deg, #f5f3ff, #ede9fe)",
            border: "1px solid #e6e0fb",
            color: "#7c3aed",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <EditNoteOutlined sx={{ fontSize: 19 }} />
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Txt
            sx={{
              fontSize: 15,
              fontWeight: 700,
              letterSpacing: "-0.01em",
              color: "#0f172a",
              display: "block",
            }}
          >
            Modified Loan Data — Loan Opening
          </Txt>
          {/* Summary preview — each stat is a filter chip over the body. */}
          <Flx
            ac
            wrap
            sx={{ gap: 0.5, mt: "3px", fontSize: 12.5, color: "#64748b" }}
          >
            {STATUS_FILTERS.map((f, i) => {
              const count = statusCounts[f.key] || 0;
              // Populated always shows (it's the headline); the other two only
              // once they have something to filter to.
              if (count === 0 && f.key !== "populated") return null;
              const active = statusFilter === f.key;
              return (
                <Fragment key={f.key}>
                  {i > 0 && (
                    <Box component="span" sx={{ color: "#cbd5e1" }}>
                      ·
                    </Box>
                  )}
                  <SummaryStat
                    count={count}
                    label={f.noun(count)}
                    active={active}
                    onClick={() => toggleStatus(f.key)}
                    title={
                      active
                        ? "Showing only these — click to show everything"
                        : `Show only ${f.noun(count)}`
                    }
                  />
                </Fragment>
              );
            })}
            {docCount > 0 && (
              <>
                <Box component="span" sx={{ color: "#cbd5e1" }}>
                  ·
                </Box>
                <SummaryStat
                  count={docCount}
                  label={docCount === 1 ? "document" : "documents"}
                  active={expanded && viewMode === "document"}
                  onClick={() => {
                    setViewMode("document");
                    setExpanded(true);
                  }}
                  title="Group the changes by source document"
                />
              </>
            )}
          </Flx>
        </Box>

        <Button
          variant="text"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            fetchRows();
          }}
          disabled={loading}
          sx={{ textTransform: "none", flexShrink: 0 }}
        >
          {loading ? "Refreshing…" : "Refresh"}
        </Button>
        <ExpandMoreRounded
          sx={{
            fontSize: 22,
            color: "#94a3b8",
            flexShrink: 0,
            transition: "transform 0.15s ease",
            transform: expanded ? "rotate(180deg)" : "none",
          }}
        />
      </Flx>

      {/* Body — a segmented switcher over three groupings of the same changes. */}
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ p: "16px 24px 20px", bgcolor: "#f8fafc" }}>
          {/* View switcher (#eef3f5 pill w/ #e3e9ec border per app convention).
              The By-field/document/List modes only apply to field rows, so the
              pill is hidden when the loan has only object creations. */}
          <Flx jb ac wrap sx={{ gap: 1, mb: 1.5 }}>
            <Flx ac wrap sx={{ gap: 1 }}>
            {fieldRows.length > 0 && (
            <Flx
              ac
              sx={{
                bgcolor: "#eef3f5",
                border: "1px solid #e3e9ec",
                borderRadius: "8px",
                p: "3px",
                gap: "2px",
              }}
            >
              {VIEW_MODES.map((m) => {
                const active = viewMode === m.key;
                return (
                  <Box
                    key={m.key}
                    component="button"
                    type="button"
                    onClick={() => setViewMode(m.key)}
                    sx={{
                      border: "none",
                      cursor: "pointer",
                      font: "inherit",
                      px: 1.25,
                      py: 0.5,
                      borderRadius: "6px",
                      fontSize: 12,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      color: active ? "#0f172a" : "#64748b",
                      bgcolor: active ? "#fff" : "transparent",
                      boxShadow: active
                        ? "0 1px 2px rgba(15,23,42,0.08)"
                        : "none",
                      "&:hover": { color: active ? "#0f172a" : "#334155" },
                    }}
                  >
                    {m.label}
                  </Box>
                );
              })}
            </Flx>
            )}

            {/* Active status chip, echoed here (and clearable) so the filter is
                visible without scrolling back to the header. */}
            {activeFilter && (
              <Chip
                size="small"
                label={`Showing ${statusCounts[activeFilter.key] || 0} ${activeFilter.noun(statusCounts[activeFilter.key] || 0)}`}
                onDelete={() => setStatusFilter(STATUS_ALL)}
                sx={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  bgcolor: "#f5f3ff",
                  color: "#7c3aed",
                  border: "1px solid #e6e0fb",
                  "& .MuiChip-deleteIcon": { color: "#a78bfa" },
                }}
              />
            )}
            </Flx>

            <TextField
              size="small"
              placeholder="Search fields, values, documents"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRounded sx={{ fontSize: 17, color: "#94a3b8" }} />
                  </InputAdornment>
                ),
                sx: { fontSize: 12.5, bgcolor: "#fff" },
              }}
              sx={{ minWidth: 220, flex: "0 1 300px" }}
            />
          </Flx>

          {/* Whole-object creations — grouped above the field-diff rows. */}
          {objectRows.length > 0 && (
            <Box sx={{ mb: fieldRows.length > 0 ? 1.75 : 0 }}>
              <Txt
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  color: "#94a3b8",
                  display: "block",
                  mb: 0.75,
                }}
              >
                Objects created ({objectRows.length})
              </Txt>
              <Box
                sx={{
                  border: "1px solid #e5e8ee",
                  borderRadius: "10px",
                  overflow: "hidden",
                  bgcolor: "#fff",
                }}
              >
                {objectRows.map((r, i) => (
                  <ObjectCreatedCard
                    key={r.provenanceId}
                    row={r}
                    first={i === 0}
                    onRemove={handleRevert}
                    onOpenDoc={handleOpenDoc}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* By document */}
          {viewMode === "document" && (
            <Flx column sx={{ gap: 1.25 }}>
              {byDocument.map((g, i) => {
                const canOpen = !!g.doc.oid;
                return (
                  <CollapsibleGroup
                    key={g.doc.id}
                    icon={DescriptionOutlined}
                    title={
                      prettyDocName(g.doc.name) ||
                      g.doc.type ||
                      "Source document"
                    }
                    titleAttr={g.doc.name}
                    defaultOpen={i < 2}
                    meta={
                      <Flx ac sx={{ gap: 0.5, flexShrink: 0 }}>
                        {g.doc.type && (
                          <Box
                            component="span"
                            sx={{
                              display: { xs: "none", sm: "inline" },
                              flexShrink: 0,
                              fontSize: 10,
                              fontWeight: 600,
                              color: "#94a3b8",
                              bgcolor: "#fff",
                              border: "1px solid #e5e8ee",
                              borderRadius: "4px",
                              px: 0.75,
                              py: "1px",
                            }}
                          >
                            {g.doc.type}
                          </Box>
                        )}
                        <CountPill n={g.rows.length} />
                      </Flx>
                    }
                    rightAction={
                      canOpen && (
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => handleOpenDoc(g.rows[0])}
                          endIcon={<OpenInNewRounded sx={{ fontSize: 13 }} />}
                          sx={{
                            textTransform: "none",
                            minWidth: 0,
                            flexShrink: 0,
                            fontSize: 11.5,
                            fontWeight: 600,
                          }}
                        >
                          View document
                        </Button>
                      )
                    }
                  >
                    {g.rows.map((r) => (
                      <ChangeRow
                        key={r.provenanceId}
                        row={r}
                        onRevert={handleRevert}
                        onOpenDoc={handleOpenDoc}
                      />
                    ))}
                  </CollapsibleGroup>
                );
              })}
            </Flx>
          )}

          {/* By field (entity key-path root) */}
          {viewMode === "field" && (
            <Flx column sx={{ gap: 1.25 }}>
              {byField.map((g, i) => (
                <CollapsibleGroup
                  key={g.key}
                  icon={GROUP_ICON[g.root] || AccountBalanceOutlined}
                  title={g.label}
                  titleAttr={g.key}
                  subtitle={g.name}
                  defaultOpen={i < 2}
                  meta={<CountPill n={g.rows.length} />}
                >
                  {g.rows.map((r) => (
                    <ChangeRow
                      key={r.provenanceId}
                      row={r}
                      onRevert={handleRevert}
                      onOpenDoc={handleOpenDoc}
                      showDoc
                      hideContext
                    />
                  ))}
                </CollapsibleGroup>
              ))}
            </Flx>
          )}

          {/* Flat list — resizable columns via the draggable header. */}
          {viewMode === "list" && fieldRows.length > 0 && (
            <Box
              sx={{
                border: "1px solid #e5e8ee",
                borderRadius: "10px",
                overflow: "hidden",
                bgcolor: "#fff",
              }}
            >
              {/* Header row with resize handles */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: listTemplate,
                  gap: "0 16px",
                  px: 2,
                  py: 1,
                  bgcolor: "#f8fafc",
                  borderBottom: "1px solid #e5e8ee",
                }}
              >
                {[
                  { label: "Field", colKey: "field" },
                  { label: "Change", colKey: "change" },
                  { label: "Source Document", colKey: "source" },
                  { label: "When", colKey: null },
                ].map((h) => (
                  <Box
                    key={h.label}
                    sx={{
                      position: "relative",
                      minWidth: 0,
                      textAlign: h.colKey ? "left" : "right",
                    }}
                  >
                    <Txt
                      sx={{
                        fontSize: 10.5,
                        fontWeight: 700,
                        letterSpacing: "0.03em",
                        textTransform: "uppercase",
                        color: "#94a3b8",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {h.label}
                    </Txt>
                    {h.colKey && (
                      <Box
                        onPointerDown={startResize(h.colKey)}
                        sx={{
                          position: "absolute",
                          top: -8,
                          bottom: -8,
                          right: -14,
                          width: 16,
                          cursor: "col-resize",
                          zIndex: 1,
                          display: "flex",
                          justifyContent: "center",
                          "&:hover > span, &:active > span": {
                            bgcolor: "#7c3aed",
                          },
                        }}
                      >
                        <Box
                          component="span"
                          sx={{ width: "2px", bgcolor: "#e3e9ec" }}
                        />
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>

              {fieldRows.map((r) => (
                <ChangeRow
                  key={r.provenanceId}
                  row={r}
                  onRevert={handleRevert}
                  onOpenDoc={handleOpenDoc}
                  showDoc
                  gridTemplate={listTemplate}
                />
              ))}
            </Box>
          )}

          {/* Nothing left after the status chip + search */}
          {filteredTotal === 0 && (
            <Flx column ac jc sx={{ py: 4, gap: 1 }}>
              <Txt sx={{ fontSize: 12.5, color: "#94a3b8" }}>
                {search.trim()
                  ? `No ${activeFilter ? activeFilter.noun(2) : "field changes"} match “${search}”.`
                  : "Nothing to show for this filter."}
              </Txt>
              {activeFilter && (
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setStatusFilter(STATUS_ALL)}
                  sx={{ textTransform: "none", fontSize: 12 }}
                >
                  Show all changes
                </Button>
              )}
            </Flx>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default OpeningFieldWritesPanel;
